import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calculator as CalcIcon, ShoppingCart, Info, Check } from 'lucide-react';
import { categories } from '../data/products';
import { useT } from '../utils/i18n';
type Shape = 'rectangle' | 'circle' | 'triangle' | 'irregular';
export function Calculator() {
  const t = useT();
  const [shape, setShape] = useState<Shape>('rectangle');
  const [unit, setUnit] = useState<'ft' | 'm'>('ft');
  const [depthUnit, setDepthUnit] = useState<'in' | 'cm'>('in');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [diameter, setDiameter] = useState('');
  const [base, setBase] = useState('');
  const [height, setHeight] = useState('');
  const [depth, setDepth] = useState('');
  const [selectedDensity, setSelectedDensity] = useState('1.4'); // default sand/gravel
  const [yards, setYards] = useState(0);
  const [tons, setTons] = useState(0);
  // Load saved
  useEffect(() => {
    const saved = localStorage.getItem('calcState');
    if (saved) {
      try {
        const p = JSON.parse(saved);
        setShape(p.shape || 'rectangle');
        setLength(p.length || '');
        setWidth(p.width || '');
        setDiameter(p.diameter || '');
        setBase(p.base || '');
        setHeight(p.height || '');
        setDepth(p.depth || '');
        setSelectedDensity(p.selectedDensity || '1.4');
      } catch (e) {}
    }
  }, []);
  // Calculate
  useEffect(() => {
    let areaSqFt = 0;
    const l = parseFloat(length) || 0;
    const w = parseFloat(width) || 0;
    const d = parseFloat(diameter) || 0;
    const b = parseFloat(base) || 0;
    const h = parseFloat(height) || 0;
    const dp = parseFloat(depth) || 0;
    // Convert to feet if meters
    const mToFt = 3.28084;
    const lFt = unit === 'm' ? l * mToFt : l;
    const wFt = unit === 'm' ? w * mToFt : w;
    const dFt = unit === 'm' ? d * mToFt : d;
    const bFt = unit === 'm' ? b * mToFt : b;
    const hFt = unit === 'm' ? h * mToFt : h;
    if (shape === 'rectangle' || shape === 'irregular') {
      areaSqFt = lFt * wFt;
    } else if (shape === 'circle') {
      const radius = dFt / 2;
      areaSqFt = Math.PI * radius * radius;
    } else if (shape === 'triangle') {
      areaSqFt = 0.5 * bFt * hFt;
    }
    // Convert depth to feet
    const dpFt = depthUnit === 'cm' ? dp / 30.48 : dp / 12;
    const cubicFeet = areaSqFt * dpFt;
    const cubicYards = cubicFeet / 27;
    setYards(Math.max(0, cubicYards));
    setTons(Math.max(0, cubicYards * parseFloat(selectedDensity)));
    // Save state
    localStorage.setItem('calcState', JSON.stringify({
      shape,
      length,
      width,
      diameter,
      base,
      height,
      depth,
      selectedDensity
    }));
  }, [shape, length, width, diameter, base, height, depth, unit, depthUnit, selectedDensity]);
  // Flatten products for density picker
  const products = categories.flatMap(c => c.products);
  const getDensity = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes('bark') || n.includes('play chips')) return '0.45';
    if (n.includes('compost')) return '0.6';
    if (n.includes('sand')) return '1.4';
    if (n.includes('topsoil') || n.includes('50/50')) return '1.1';
    return '1.3'; // default rock
  };
  const inputClass = 'w-full px-4 py-3 bg-white dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#333] rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all';
  return <main className="py-12 md:py-20 bg-[#fdfbf7] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)]" data-id="element-1373">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1374">
        <div className="mb-8 text-center md:text-left" data-id="element-1375">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white flex items-center justify-center md:justify-start" data-id="element-1376">
            <CalcIcon className="w-8 h-8 mr-3 text-brand-orange" data-id="element-1377" />
            {t('calc.title')}
          </h1>
          <p className="text-stone-500 dark:text-stone-400 mt-2 text-lg" data-id="element-1378">
            {t('calc.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1379">
          {/* Left Col: Inputs */}
          <div className="lg:col-span-2 space-y-6" data-id="element-1380">
            {/* Shape Tabs */}
            <div className="bg-white dark:bg-[#222] p-2 rounded-2xl border border-stone-200 dark:border-[#333] flex flex-wrap sm:flex-nowrap gap-2" data-id="element-1381">
              {(['rectangle', 'circle', 'triangle', 'irregular'] as Shape[]).map(s => <button key={s} onClick={() => setShape(s)} className={`relative flex-1 py-3 px-4 text-sm font-bold rounded-xl transition-colors z-10 ${shape === s ? 'text-white' : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-200'}`} data-id="element-1382">
                  {shape === s && <motion.div layoutId="shapeTab" className="absolute inset-0 bg-brand-charcoal dark:bg-[#444] rounded-xl -z-10" transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-1383" />}
                  {t(`calc.shape.${s}`)}
                </button>)}
            </div>

            <div className="bg-white dark:bg-[#222] p-6 sm:p-8 rounded-2xl border border-stone-200 dark:border-[#333] shadow-sm" data-id="element-1384">
              <div className="flex justify-end mb-4" data-id="element-1385">
                <div className="flex bg-stone-100 dark:bg-[#1a1a1a] rounded-lg p-1 border border-stone-200 dark:border-[#333]" data-id="element-1386">
                  <button onClick={() => setUnit('ft')} className={`px-3 py-1 text-xs font-bold rounded-md ${unit === 'ft' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1387">
                    Feet
                  </button>
                  <button onClick={() => setUnit('m')} className={`px-3 py-1 text-xs font-bold rounded-md ${unit === 'm' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1388">
                    Meters
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1389">
                <AnimatePresence mode="wait" data-id="element-1390">
                  {(shape === 'rectangle' || shape === 'irregular') && <motion.div key="rect" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1391">
                      <div data-id="element-1392">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1393">
                          {t('calc.length')} ({unit})
                        </label>
                        <input type="number" value={length} onChange={e => setLength(e.target.value)} className={inputClass} placeholder="0" data-id="element-1394" />
                      </div>
                      <div data-id="element-1395">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1396">
                          {t('calc.width')} ({unit})
                        </label>
                        <input type="number" value={width} onChange={e => setWidth(e.target.value)} className={inputClass} placeholder="0" data-id="element-1397" />
                      </div>
                    </motion.div>}

                  {shape === 'circle' && <motion.div key="circ" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="col-span-1 sm:col-span-2" data-id="element-1398">
                      <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1399">
                        {t('calc.diameter')} ({unit})
                      </label>
                      <input type="number" value={diameter} onChange={e => setDiameter(e.target.value)} className={inputClass} placeholder="0" data-id="element-1400" />
                    </motion.div>}

                  {shape === 'triangle' && <motion.div key="tri" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1401">
                      <div data-id="element-1402">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1403">
                          {t('calc.base')} ({unit})
                        </label>
                        <input type="number" value={base} onChange={e => setBase(e.target.value)} className={inputClass} placeholder="0" data-id="element-1404" />
                      </div>
                      <div data-id="element-1405">
                        <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1406">
                          {t('calc.height')} ({unit})
                        </label>
                        <input type="number" value={height} onChange={e => setHeight(e.target.value)} className={inputClass} placeholder="0" data-id="element-1407" />
                      </div>
                    </motion.div>}
                </AnimatePresence>

                <div className="col-span-1 sm:col-span-2 border-t border-stone-100 dark:border-[#333] pt-6 mt-2" data-id="element-1408">
                  <div className="flex justify-between items-end mb-2" data-id="element-1409">
                    <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider" data-id="element-1410">
                      {t('calc.depth')}
                    </label>
                    <div className="flex bg-stone-100 dark:bg-[#1a1a1a] rounded-lg p-1 border border-stone-200 dark:border-[#333]" data-id="element-1411">
                      <button onClick={() => setDepthUnit('in')} className={`px-3 py-1 text-xs font-bold rounded-md ${depthUnit === 'in' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1412">
                        Inches
                      </button>
                      <button onClick={() => setDepthUnit('cm')} className={`px-3 py-1 text-xs font-bold rounded-md ${depthUnit === 'cm' ? 'bg-white dark:bg-[#333] shadow-sm text-stone-900 dark:text-white' : 'text-stone-500'}`} data-id="element-1413">
                        cm
                      </button>
                    </div>
                  </div>
                  <input type="number" value={depth} onChange={e => setDepth(e.target.value)} className={inputClass} placeholder="0" data-id="element-1414" />
                </div>

                <div className="col-span-1 sm:col-span-2" data-id="element-1415">
                  <label className="block text-xs font-bold text-stone-700 dark:text-stone-400 uppercase tracking-wider mb-2" data-id="element-1416">
                    Material Type (for ton estimate)
                  </label>
                  <select value={selectedDensity} onChange={e => setSelectedDensity(e.target.value)} className={inputClass} data-id="element-1417">
                    <option value="1.4" data-id="element-1418">Sand / Gravel (~1.4 tons/yd)</option>
                    <option value="1.3" data-id="element-1419">Crushed Rock (~1.3 tons/yd)</option>
                    <option value="1.1" data-id="element-1420">Topsoil (~1.1 tons/yd)</option>
                    <option value="0.6" data-id="element-1421">Compost (~0.6 tons/yd)</option>
                    <option value="0.45" data-id="element-1422">Bark / Mulch (~0.45 tons/yd)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Right Col: Results */}
          <div className="space-y-6" data-id="element-1423">
            <div className="bg-brand-charcoal dark:bg-[#222] rounded-2xl border border-brand-charcoal dark:border-[#333] p-8 shadow-xl text-center relative overflow-hidden" data-id="element-1424">
              <div className="absolute top-0 left-0 w-full h-1 bg-brand-orange" data-id="element-1425" />

              <p className="text-stone-400 uppercase tracking-widest text-xs font-bold mb-2" data-id="element-1426">
                {t('calc.result.yards')}
              </p>
              <div className="text-6xl font-heading font-extrabold text-white mb-6" data-id="element-1427">
                {yards > 0 ? yards.toFixed(1) : '0.0'}
              </div>

              <div className="border-t border-stone-700 dark:border-[#333] pt-6 mb-6" data-id="element-1428">
                <p className="text-stone-400 uppercase tracking-widest text-xs font-bold mb-2" data-id="element-1429">
                  {t('calc.result.tons')}
                </p>
                <div className="text-3xl font-heading font-bold text-stone-200" data-id="element-1430">
                  {tons > 0 ? tons.toFixed(1) : '0.0'}
                </div>
              </div>

              <Link to="/#materials" className="w-full py-4 rounded-xl font-bold transition-all flex items-center justify-center bg-brand-orange hover:bg-[#e66a2c] text-white shadow-lg shadow-brand-orange/20" data-id="element-1431">
                <ShoppingCart className="w-5 h-5 mr-2" data-id="element-1432" />
                Shop Materials
              </Link>
            </div>

            {/* Visual Diagram */}
            <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] p-6 shadow-sm flex items-center justify-center h-48" data-id="element-1433">
              <svg width="120" height="120" viewBox="0 0 100 100" className="text-stone-200 dark:text-[#333]" data-id="element-1434">
                {shape === 'rectangle' || shape === 'irregular' ? <rect x="10" y="20" width="80" height="60" fill="currentColor" stroke="#F87431" strokeWidth="2" data-id="element-1435" /> : shape === 'circle' ? <circle cx="50" cy="50" r="40" fill="currentColor" stroke="#F87431" strokeWidth="2" data-id="element-1436" /> : <polygon points="50,10 90,90 10,90" fill="currentColor" stroke="#F87431" strokeWidth="2" data-id="element-1437" />}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </main>;
}